import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import BirthDateModal from './BirthDateModal';

const MONTHS_DE_LONG = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** API: MM-DD oder Legacy YYYY-MM-DD */
function formatBirthDisplay(mdOrYmd) {
  if (!mdOrYmd) return 'Tag & Monat wählen…';
  const t = String(mdOrYmd).includes('T') ? String(mdOrYmd).split('T')[0] : String(mdOrYmd);
  const p = t.split('-').map(Number);
  let m;
  let d;
  if (p.length === 2) {
    [m, d] = p;
  } else if (p.length === 3) {
    [, m, d] = p;
  } else {
    return mdOrYmd;
  }
  if (!m || !d) return mdOrYmd;
  return `${d}. ${MONTHS_DE_LONG[m - 1] || m}`;
}

const AccountModal = ({ isOpen, onClose, member, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birth_date: '',
    sound_enabled: true
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showBirthPicker, setShowBirthPicker] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    type: 'bug',
    title: '',
    description: '',
  });
  const [feedbackState, setFeedbackState] = useState({
    isSubmitting: false,
    error: null,
    success: null,
    issueUrl: null,
  });

  /**
   * true = Formular wurde für diese „Modalsession“ schon aus `member` befüllt.
   * Beim Schließen zurücksetzen, damit beim nächsten Öffnen wieder geladen wird.
   */
  const formHydratedForOpenRef = useRef(false);

  // Formular nur einmal pro geöffnetem Modal aus `member` füllen — nicht bei jedem
  // Parent-Update von `member` (z. B. nach Speichern), sonst werden die Felder überschrieben.
  useEffect(() => {
    if (!isOpen) {
      formHydratedForOpenRef.current = false;
      return;
    }
    if (member && !formHydratedForOpenRef.current) {
      setFormData({
        username: member.user_username || '',
        first_name: member.user_first_name || '',
        last_name: member.user_last_name || '',
        email: member.user_email || '',
        phone: member.phone || '',
        birth_date: member.birth_date || '',
        sound_enabled: member.sound_enabled !== false,
      });
      formHydratedForOpenRef.current = true;
    }
  }, [isOpen, member]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const cleanData = {
        ...formData,
        birth_date: formData.birth_date || null
      };
      const response = await apiService.updateProfile(cleanData);
      setSuccess('Profil erfolgreich aktualisiert!');
      if (response?.data?.member) {
        const updatedMember = response.data.member;
        setFormData({
          username: updatedMember.user_username || '',
          first_name: updatedMember.user_first_name || '',
          last_name: updatedMember.user_last_name || '',
          email: updatedMember.user_email || '',
          phone: updatedMember.phone || '',
          birth_date: updatedMember.birth_date || '',
          sound_enabled: updatedMember.sound_enabled !== false,
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          ...cleanData,
          birth_date: cleanData.birth_date || '',
        }));
      }
      if (onProfileUpdate) {
        if (response?.data?.member) {
          onProfileUpdate(response.data.member);
        } else {
          onProfileUpdate({
            ...member,
            user_username: cleanData.username,
            user_first_name: cleanData.first_name,
            user_last_name: cleanData.last_name,
            user_email: cleanData.email,
            phone: cleanData.phone,
            birth_date: cleanData.birth_date,
            sound_enabled: cleanData.sound_enabled,
          });
        }
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Profils');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Die neuen Passwörter stimmen nicht überein');
      setIsLoading(false);
      return;
    }

    try {
      await apiService.changePassword(passwordData);
      setSuccess('Passwort erfolgreich geändert!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern des Passworts');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 rounded-xl border-2 border-accent-1/20 bg-[#f5f0e8] text-bg-1 placeholder:text-bg-1/50 focus:border-accent-1 focus:ring-2 focus:ring-accent-1/20 outline-none transition-colors';
  /** Select ohne Browser-Standardrahmen, Chevron wie beim Geburtstagsfeld */
  const selectClass = `${inputClass} appearance-none cursor-pointer pr-11 hover:border-accent-1/50`;
  const labelClass = 'block text-sm font-semibold text-bg-1 mb-1';

  const handleOpenTerms = async () => {
    try {
      const data = await apiService.getTerms();
      if (data.success) {
        setTermsText(data.terms || 'Keine Satzung hinterlegt.');
      } else {
        setTermsText('Satzung konnte nicht geladen werden.');
      }
    } catch (_e) {
      setTermsText('Satzung konnte nicht geladen werden.');
    }
    setShowTermsModal(true);
  };

  const handleFeedbackInputChange = (e) => {
    const { name, value } = e.target;
    setFeedbackData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    setFeedbackState({ isSubmitting: true, error: null, success: null, issueUrl: null });
    try {
      const result = await apiService.submitFeedbackTicket(feedbackData);
      setFeedbackState({
        isSubmitting: false,
        error: null,
        success: `Ticket erfolgreich erstellt (#${result.issue_number}).`,
        issueUrl: result.issue_url || null,
      });
      setFeedbackData({ type: 'bug', title: '', description: '' });
    } catch (err) {
      setFeedbackState({
        isSubmitting: false,
        error: err.response?.data?.error || 'Ticket konnte nicht erstellt werden.',
        success: null,
        issueUrl: null,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-accent-3 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl border-2 border-accent-1/30">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">👤</div>
            <h2 className="text-2xl font-heading font-bold text-bg-1 mb-2">Konto-Einstellungen</h2>
            <p className="text-bg-1/80 text-sm">Verwalte deine persönlichen Daten</p>
          </div>

          {error && (
            <div className="bg-status-error/15 border border-status-error/40 rounded-lg p-3 mb-4">
              <p className="text-status-error text-sm font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-status-success/15 border border-status-success/40 rounded-lg p-3 mb-4">
              <p className="text-status-success text-sm font-medium">{success}</p>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-lg font-heading font-bold text-bg-1 mb-4">Persönliche Daten</h3>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>Benutzername</label>
                  <input type="text" name="username" value={formData.username} onChange={handleInputChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Vorname</label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Nachname</label>
                  <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} className={inputClass} />
                </div>
              </div>

              <div>
                <label className={labelClass}>E-Mail</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Telefonnummer</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Geburtstag (Tag &amp; Monat)</label>
                <button
                  type="button"
                  onClick={() => setShowBirthPicker(true)}
                  className={`${inputClass} text-left flex items-center justify-between cursor-pointer hover:border-accent-1/50`}
                >
                  <span className={formData.birth_date ? 'text-bg-1' : 'text-text-subtle'}>
                    {formatBirthDisplay(formData.birth_date)}
                  </span>
                  <span className="text-accent-2 text-lg" aria-hidden>📅</span>
                </button>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="sound_enabled"
                  checked={formData.sound_enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-accent-1 text-accent-1 focus:ring-accent-1"
                />
                <label className="ml-2 block text-sm text-bg-1 font-medium">Soundeffekte aktivieren</label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Speichere…' : 'Profil speichern'}
              </button>
            </form>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-heading font-bold text-bg-1 mb-4">Passwort ändern</h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className={labelClass}>Aktuelles Passwort</label>
                <input type="password" name="current_password" value={passwordData.current_password} onChange={handlePasswordChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Neues Passwort</label>
                <input type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Neues Passwort bestätigen</label>
                <input type="password" name="confirm_password" value={passwordData.confirm_password} onChange={handlePasswordChange} className={inputClass} />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Ändere…' : 'Passwort ändern'}
              </button>
            </form>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-heading font-bold text-bg-1 mb-3">Satzung</h3>
            <p className="text-sm text-bg-1/80 mb-3">Die Satzung ist hier jederzeit einsehbar.</p>
            <button
              type="button"
              onClick={handleOpenTerms}
              className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Satzung anzeigen
            </button>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-heading font-bold text-bg-1 mb-4">Bug melden / Feature vorschlagen</h3>
            <form onSubmit={handleSubmitFeedback} className="space-y-4">
              <div>
                <label className={labelClass}>Typ des Tickets</label>
                <div className="relative">
                  <select
                    name="type"
                    value={feedbackData.type}
                    onChange={handleFeedbackInputChange}
                    className={selectClass}
                  >
                    <option value="bug">Bug</option>
                    <option value="feature">Feature Request</option>
                  </select>
                  <span
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-accent-2 text-lg leading-none"
                    aria-hidden
                  >
                    ▾
                  </span>
                </div>
              </div>
              <div>
                <label className={labelClass}>Titel</label>
                <input
                  type="text"
                  name="title"
                  value={feedbackData.title}
                  onChange={handleFeedbackInputChange}
                  className={inputClass}
                  placeholder="Kurz und prägnant"
                  required
                  minLength={5}
                />
              </div>
              <div>
                <label className={labelClass}>Beschreibung</label>
                <textarea
                  name="description"
                  value={feedbackData.description}
                  onChange={handleFeedbackInputChange}
                  className={inputClass}
                  rows="4"
                  placeholder="Was ist passiert / Was wünschst du dir?"
                  required
                  minLength={10}
                />
              </div>
              {feedbackState.error && (
                <div className="bg-status-error/15 border border-status-error/40 rounded-lg p-3">
                  <p className="text-status-error text-sm font-medium">{feedbackState.error}</p>
                </div>
              )}
              {feedbackState.success && (
                <div className="bg-status-success/15 border border-status-success/40 rounded-lg p-3">
                  <p className="text-status-success text-sm font-medium">{feedbackState.success}</p>
                  {feedbackState.issueUrl && (
                    <a
                      href={feedbackState.issueUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm underline text-bg-1"
                    >
                      Issue auf GitHub öffnen
                    </a>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={feedbackState.isSubmitting}
                className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {feedbackState.isSubmitting ? 'Sende…' : 'Ticket erstellen'}
              </button>
            </form>
          </div>

          <div className="pt-6 border-t border-accent-1/20">
            <h3 className="text-lg font-heading font-bold text-bg-1 mb-3">Sitzung beenden</h3>
            <p className="text-sm text-bg-1/90 mb-4">
              Möchtest du dich ausloggen? Du musst dich danach erneut anmelden.
            </p>
            <button
              onClick={async () => {
                if (window.confirm('Möchtest du dich wirklich ausloggen?')) {
                  try {
                    await apiService.logout();
                  } catch (err) {
                    console.error(err);
                  }
                  window.location.reload();
                }
              }}
              className="w-full bg-status-error hover:opacity-90 text-white font-semibold py-3 px-4 rounded-lg mb-3"
            >
              Ausloggen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>

      <BirthDateModal
        isOpen={showBirthPicker}
        value={formData.birth_date}
        onSelect={(ymd) => setFormData((prev) => ({ ...prev, birth_date: ymd || '' }))}
        onClose={() => setShowBirthPicker(false)}
      />
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-accent-3 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] shadow-xl border-2 border-accent-1/30 flex flex-col">
            <h3 className="text-xl font-heading font-bold text-bg-1 mb-3">Satzung</h3>
            <div className="overflow-y-auto whitespace-pre-wrap text-sm text-bg-1 bg-[#f5f0e8] rounded-xl border border-accent-1/20 p-4 mb-4">
              {termsText}
            </div>
            <button
              type="button"
              onClick={() => setShowTermsModal(false)}
              className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountModal;
